import React, { useEffect, useState } from 'react'

import PropTypes from 'prop-types'

import { FormControlLabel, Switch } from '@material-ui/core'
import { observer } from 'mobx-react'

import { useStores } from 'retailer/stores/hooks'

/**
 * Staff-only inline toggle for a single staff-pick flag (e.g. `category_pick`).
 *
 * Renders nothing for non-staff users. Toggling optimistically updates the
 * switch and immediately persists the change via `productStore.setStaffPick`,
 * rolling back if the request fails.
 */
const StaffPickToggle = ({ productStore, flag, label, className }) => {
  const stores = useStores()
  const { staffPicks, setStaffPick } = productStore

  const [checked, setChecked] = useState(!!staffPicks?.includes(flag))
  useEffect(() => setChecked(!!staffPicks?.includes(flag)), [staffPicks, flag])

  if (!stores?.userProfileStore?.isStaff) return null

  const handleChange = async ev => {
    // Slates wrap most content in a link; keep the toggle from navigating.
    ev.preventDefault()
    ev.stopPropagation()
    const next = !checked
    setChecked(next)
    try {
      await setStaffPick(flag, next)
    } catch (error) {
      setChecked(!next)
    }
  }

  return (
    <FormControlLabel
      className={className}
      onClick={ev => ev.stopPropagation()}
      control={<Switch size="small" color="primary" checked={checked} onChange={handleChange} />}
      label={label}
      data-testid={`StaffPickToggle-${flag}`}
    />
  )
}

StaffPickToggle.propTypes = {
  productStore: PropTypes.shape({
    staffPicks: PropTypes.arrayOf(PropTypes.string),
    setStaffPick: PropTypes.func.isRequired,
  }).isRequired,
  flag: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
}

export default observer(StaffPickToggle)
